import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Download, Printer, Search } from 'lucide-react'
import { getTrialBalanceGaap } from '../../api/accounting'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip, LabelWithHelp } from '../../components/HelpTip'
import { StatusBadge } from '../../components/PageParts'
import { fmtDay, localToday } from '../../utils/format'
import { money } from '../sales/salesShared'
import { ReportsTabs, StatementHead, glHref, useCompanyName } from './reportsShared'
import { printPage, useAutoPrint } from '../../utils/print'

const TYPES = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense']
const PLURAL = { Asset: 'Assets', Liability: 'Liabilities', Equity: 'Equity', Revenue: 'Revenue', Expense: 'Expenses', Other: 'Not in the chart of accounts' }

// Trial balance: every account's balance on a date, debits and credits.
export default function TrialBalance() {
  const company = useCompanyName()
  const [params, setParams] = useSearchParams()
  const asOf = params.get('as_of') || localToday()
  const beforeClosing = params.get('closing') === 'exclude'
  const [data, setData] = useState(null)
  useAutoPrint(!!data)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const set = (patch) => setParams((p) => { const n = new URLSearchParams(p); Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); return n }, { replace: true })

  useEffect(() => {
    setError('')
    setData(null)
    getTrialBalanceGaap({ as_of: asOf, closing: beforeClosing ? 'exclude' : 'include' })
      .then(setData)
      .catch((e) => setError(e.message || 'Could not load the trial balance.'))
  }, [asOf, beforeClosing])

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase()
    const rows = (data?.rows || []).filter((r) => !term || `${r.code} ${r.name} ${r.section}`.toLowerCase().includes(term))
    return TYPES.map((t) => [t, rows.filter((r) => r.type === t)]).concat([['Other', rows.filter((r) => !TYPES.includes(r.type))]]).filter(([, l]) => l.length)
  }, [data, search])

  const exportCsv = () => {
    const lines = [['Code', 'Account', 'Type', 'Statement section', 'Debit', 'Credit'],
      ...(data?.rows || []).map((r) => [r.code, r.name, r.type, r.section, r.debit ? r.debit.toFixed(2) : '', r.credit ? r.credit.toFixed(2) : '']),
      ['', 'Total', '', '', data.total_debit.toFixed(2), data.total_credit.toFixed(2)]]
    const csv = lines.map((a) => a.map((x) => `"${String(x ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `trial_balance_${asOf}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ci-page fs-page">
      <ReportsTabs />
      <div className="ci-header-row fs-noprint">
        <div className="ci-title-block">
          <div>
            <h1>Trial balance <HelpTip k="trialBalance" /></h1>
            <div className="ci-title-meta">Every account’s balance on a date.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={exportCsv}><Download size={15} />Export</button>
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => printPage()}><Printer size={15} />Print</button>
        </div>
      </div>

      <div className="ci-toolbar fs-noprint">
        <label className="fs-inline">As of <input className="ci-select" type="date" value={asOf} onChange={(e) => set({ as_of: e.target.value })} /></label>
        <label className="ci-search" aria-label="Search accounts">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Account code or name…" />
        </label>
        <label className="ci-form-toggle fs-inline">
          <input type="checkbox" checked={beforeClosing} onChange={(e) => set({ closing: e.target.checked ? 'exclude' : '' })} />
          <LabelWithHelp label="Before closing entries" k="beforeClosing" />
        </label>
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? (!error && <Spinner label="Preparing the trial balance…" />) : (
        <section className="fs-card">
          <StatementHead company={company} title={beforeClosing ? 'Trial balance (before closing entries)' : 'Trial balance'} sub={`As of ${fmtDay(data.as_of)}`} />
          <div className="fs-check fs-noprint">
            {Math.abs(data.difference) < 0.005
              ? <StatusBadge label="Debits = credits" tone="good" />
              : <StatusBadge label={`Out by ${money(Math.abs(data.difference))}`} tone="bad" />}
          </div>
          <table className="fs-table fs-tb">
            <thead><tr><th>Account</th><th className="hide-sm">Statement section</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
            <tbody>
              {groups.map(([type, rows]) => (
                <FragmentRows key={type} type={type} rows={rows} asOf={asOf} />
              ))}
              <tr className="fs-grand">
                <td>Total</td><td className="hide-sm" />
                <td className="num">{money(data.total_debit)}</td>
                <td className="num">{money(data.total_credit)}</td>
              </tr>
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}

function FragmentRows({ type, rows, asOf }) {
  return (
    <>
      <tr className="fs-subheading"><th colSpan="4" scope="rowgroup">{PLURAL[type]}</th></tr>
      {rows.map((r) => (
        <tr key={r.code} className="fs-line">
          <td><Link className="fs-link" to={glHref(r.code, null, asOf)}>{r.code} · {r.name}</Link></td>
          <td className="hide-sm fs-muted">{r.section}</td>
          <td className="num">{r.debit ? money(r.debit) : ''}</td>
          <td className="num">{r.credit ? money(r.credit) : ''}</td>
        </tr>
      ))}
    </>
  )
}
